import React, { useState } from "react";
import { auth, db } from "../firebase";
import { doc, updateDoc } from "firebase/firestore";
import { useTheme } from "../context/ThemeContext";

export default function UserProfileViewer({ photoURL, onClose }) {
    const [uploading, setUploading] = useState(false);
    const [currentPhoto, setCurrentPhoto] = useState(photoURL);
    const { isDarkMode } = useTheme();
    const uid = auth.currentUser?.uid;

    const resizeImage = (file) => {
        return new Promise((resolve) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = (event) => {
                const img = new Image();
                img.src = event.target.result;
                img.onload = () => {
                    const canvas = document.createElement("canvas");
                    const MAX_WIDTH = 400; // Better quality for DP
                    const scaleSize = MAX_WIDTH / img.width;
                    canvas.width = MAX_WIDTH;
                    canvas.height = img.height * scaleSize;

                    const ctx = canvas.getContext("2d");
                    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

                    const dataUrl = canvas.toDataURL("image/jpeg", 0.8);
                    resolve(dataUrl);
                };
            };
        });
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setUploading(true);
        try {
            const base64Image = await resizeImage(file);
            await updateDoc(doc(db, "users", uid), {
                photoURL: base64Image
            });
            setCurrentPhoto(base64Image);
            setUploading(false);
        } catch (err) {
            console.error("Image upload failed:", err);
            setUploading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex justify-center items-center z-[60] animate-fade-in" onClick={onClose}>
            <div className="relative max-w-lg w-full p-6 flex flex-col items-center" onClick={(e) => e.stopPropagation()}>

                {/* Close Button */}
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-white/70 hover:text-white p-2 rounded-full hover:bg-white/10 transition-colors"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                </button>

                {/* Header */}
                <h2 className="text-white text-xl font-medium mb-8 tracking-wide">Profile Photo</h2>

                {/* Large Image */}
                <div className="relative group">
                    <img
                        src={currentPhoto || "https://via.placeholder.com/300"}
                        alt="Profile"
                        className="w-64 h-64 md:w-80 md:h-80 rounded-full object-cover shadow-2xl border-4 border-white/10"
                    />

                    {/* Upload Overlay */}
                    {uploading ? (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/60 rounded-full">
                            <div className="flex flex-col items-center">
                                <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-2"></div>
                                <span className="text-white text-sm font-medium">Updating...</span>
                            </div>
                        </div>
                    ) : (
                        <div className="absolute inset-x-0 bottom-0 h-1/4 bg-gray-900/40 rounded-b-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity backdrop-blur-sm cursor-pointer">
                            <label className="flex items-center gap-2 cursor-pointer w-full h-full justify-center">
                                <input type="file" className="hidden" accept="image/*" onChange={handleImageUpload} />
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-white"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path><circle cx="12" cy="13" r="4"></circle></svg>
                                <span className="text-white font-medium text-sm">Change Photo</span>
                            </label>
                        </div>
                    )}
                </div>

                <p className="mt-6 text-white/50 text-sm">Tap on the picture to change it</p>

            </div>
        </div>
    );
}
